/* =============================================================================
 *  ApprovalsContext  (Unified approvals inbox — P0: read-only aggregation)
 *
 *  Federated, not centralized: this does NOT own a new collection or any execute
 *  path. It live-subscribes the three existing pending streams, normalizes each
 *  into one in-memory view-model, and exposes the open/close state for a global
 *  side-pane + a header badge count that flashes when a new item arrives.
 *
 *  Sources (read-only here; Approve/Open actions route back to each domain's own
 *  gated function in P1 — never a generic execute that bypasses the GAds holy
 *  rule / kill switch, the broker capability gates, or the money invariants):
 *    - googleAdsChangeSets  (status in pending_review/applying/failed)  → GAds
 *    - agent_pending_actions (status == pending)                        → broker/Andrei
 *    - email_drafts          (userId == me, status == draft)            → review & send
 * ============================================================================= */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where, limit, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../firebase';

export type ApprovalSource = 'gads' | 'agentPending' | 'draftEmail' | 'agentGrants';

export interface ApprovalItem {
  id: string;
  source: ApprovalSource;
  kind: string;                       // raw discriminator (op type / action kind)
  title: string;
  summary: string;
  risk: 'low' | 'medium' | 'high';
  status: string;
  createdAtMs: number;
  raw: any;                           // original doc data — for P1 renderers + dispatch
}

interface ApprovalsContextValue {
  isOpen: boolean;
  openApprovals: () => void;
  closeApprovals: () => void;
  items: ApprovalItem[];
  count: number;
  /** Brief true after the count increases — drives the header flash. */
  flash: boolean;
  /** Google Ads master kill switch — GAds approvals are blocked while OFF. */
  writesOn: boolean;
}

// Only GAds change-sets AWAITING APPROVAL belong in the badge — 'applying' is
// transient and 'failed' is an error state (handled in the change-activity panel),
// neither is a pending approval. (The full inbox lives in ChangeActivityPanel.)
const GADS_APPROVAL_STATUS = 'pending_review';

const toMs = (v: any): number => {
  try { if (v && typeof v.toMillis === 'function') return v.toMillis(); } catch { /* not a Timestamp */ }
  if (typeof v === 'number') return v;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? 0 : t; }
  return 0;
};

/** 'invoice.create' → 'Create invoice'; 'campaign.setBudget' → 'Set budget campaign'. */
const humanizeKind = (kind: string): string => {
  const parts = String(kind || '').split('.');
  if (parts.length === 2) {
    const [noun, verb] = parts;
    const v = verb.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
    return `${v.charAt(0).toUpperCase()}${v.slice(1)} ${noun}`.trim();
  }
  return kind || 'action';
};

const ApprovalsContext = createContext<ApprovalsContextValue>({
  isOpen: false, openApprovals: () => {}, closeApprovals: () => {},
  items: [], count: 0, flash: false, writesOn: false,
});

export const ApprovalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [gads, setGads] = useState<ApprovalItem[]>([]);
  const [pending, setPending] = useState<ApprovalItem[]>([]);
  const [drafts, setDrafts] = useState<ApprovalItem[]>([]);
  const [grants, setGrants] = useState<ApprovalItem[]>([]);
  const [flash, setFlash] = useState(false);
  const [writesOn, setWritesOn] = useState(false);

  // GAds master kill switch — mirrors ChangeActivityPanel so the inbox can block
  // a GAds approve while writes are disarmed (applyChangeSet would reject anyway).
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'google_ads_writes_enabled'),
      (snap) => setWritesOn(snap.exists() ? (snap.data() as any).enabled === true : false),
      () => { /* best-effort */ });
    return () => unsub();
  }, []);

  // ── Google Ads change-sets (global inbox). Read is allowed; writes are
  //    backend-only — Approve will route through decideGoogleAdsChangeSet in P1.
  useEffect(() => {
    const q = query(collection(db, 'googleAdsChangeSets'), where('status', '==', GADS_APPROVAL_STATUS), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      setGads(snap.docs.map((d): ApprovalItem => {
        const x: any = d.data() || {};
        const ops = Array.isArray(x.operations) ? x.operations : [];
        const nOps = ops.length || (x.plan ? 1 : 0);
        return {
          id: d.id, source: 'gads', kind: 'gads.changeSet',
          title: String(x.summary || (nOps ? `${nOps} Google Ads change${nOps === 1 ? '' : 's'}` : 'Google Ads change')),
          summary: `${x.accountId ? `acct ${x.accountId} · ` : ''}${nOps} op${nOps === 1 ? '' : 's'}${x.source ? ` · ${x.source}` : ''}`,
          risk: (x.risk === 'high' || x.risk === 'low') ? x.risk : 'medium',
          status: String(x.status || ''),
          createdAtMs: toMs(x.createdAt),
          raw: x,
        };
      }));
    }, () => setGads([]));
    return () => unsub();
  }, []);

  // ── Agent broker / Andrei force-queued actions.
  useEffect(() => {
    const q = query(collection(db, 'agent_pending_actions'), where('status', '==', 'pending'), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      setPending(snap.docs.map((d): ApprovalItem => {
        const x: any = d.data() || {};
        const kind = String(x.kind || x.actionKind || 'action');
        return {
          id: d.id, source: 'agentPending', kind,
          title: humanizeKind(kind),
          summary: String(x.summary || x.reason || x.rationale || (x.payload?.customerId ? `customer ${Array.isArray(x.payload.customerId) ? x.payload.customerId.join(', ') : x.payload.customerId}` : '')),
          risk: 'medium',
          status: 'pending',
          createdAtMs: toMs(x.createdAt),
          raw: x,
        };
      }));
    }, () => setPending([]));
    return () => unsub();
  }, []);

  // ── Email drafts (owner-scoped). Clicking will OPEN the composer (P1) — drafts
  //    are review-and-send, never approve-and-auto-send.
  useEffect(() => {
    let unsubDocs = () => {};
    const stopAuth = onAuthStateChanged(auth, (u) => {
      unsubDocs();
      if (!u) { setDrafts([]); return; }
      const q = query(
        collection(db, 'email_drafts'),
        where('userId', '==', u.uid),
        where('status', '==', 'draft'),
        limit(50),
      );
      unsubDocs = onSnapshot(q, (snap) => {
        setDrafts(snap.docs.map((d): ApprovalItem => {
          const x: any = d.data() || {};
          const to = Array.isArray(x.to) ? x.to.filter(Boolean).join(', ') : (typeof x.to === 'string' ? x.to : '');
          return {
            id: d.id, source: 'draftEmail', kind: 'email.draft',
            title: String(x.subject || '(no subject)'),
            summary: to ? `to ${to}` : 'draft email',
            risk: 'low',
            status: 'draft',
            createdAtMs: toMs(x.updatedAt || x.createdAt),
            raw: x,
          };
        }));
      }, () => setDrafts([]));
    });
    return () => { stopAuth(); unsubDocs(); };
  }, []);

  // ── Broker access grants (4th source). Owner/admin-only: the agentGrants rule
  //    gates read on users.role=='admin', so a non-admin's listener errors → [].
  //    Surfacing only — clicking opens the dedicated grant-approval modal (TTL +
  //    scope review), NEVER a one-tap approve of a trust-boundary crossing.
  useEffect(() => {
    let unsubDocs = () => {};
    const stopAuth = onAuthStateChanged(auth, (u) => {
      unsubDocs();
      if (!u) { setGrants([]); return; }
      const q = query(
        collection(db, 'agentGrants'),
        where('status', '==', 'pending'),
        limit(50),
      );
      unsubDocs = onSnapshot(q, (snap) => {
        setGrants(snap.docs.map((d): ApprovalItem => {
          const x: any = d.data() || {};
          const caps: string[] = Array.isArray(x.capabilities) ? x.capabilities : [];
          const reqScope = String(x.requestedScope || x.scope || 'none');
          // A grant is a trust-boundary crossing — at least medium. Propose-level
          // Ads access or any high-risk / write capability bumps it to high.
          const highRisk = reqScope === 'read_propose'
            || caps.some((c) => c === 'email:bodies' || c === 'web:fetch' || c === 'crm:write');
          const scopeBits = [
            reqScope !== 'none' ? `${reqScope.replace('_', '+')} Google Ads` : '',
            caps.length ? `${caps.length} capabilit${caps.length === 1 ? 'y' : 'ies'}` : '',
          ].filter(Boolean).join(' · ');
          return {
            id: d.id, source: 'agentGrants', kind: 'broker.grant',
            title: String(x.label || 'Agent access request'),
            summary: [x.purpose, scopeBits].filter(Boolean).join(' — ') || 'external agent requesting access',
            risk: highRisk ? 'high' : 'medium',
            status: 'pending',
            createdAtMs: toMs(x.createdAtMs || x.createdAt),
            raw: x,
          };
        }));
      }, () => setGrants([]));
    });
    return () => { stopAuth(); unsubDocs(); };
  }, []);

  const items = useMemo(
    () => [...gads, ...pending, ...drafts, ...grants].sort((a, b) => b.createdAtMs - a.createdAtMs),
    [gads, pending, drafts, grants],
  );
  const count = items.length;

  // Flash the header badge when the count grows (a new proposal landed). Skip the
  // first settle so it doesn't flash on initial load.
  const prevCount = useRef<number>(0);
  const settled = useRef(false);
  useEffect(() => {
    if (!settled.current) { settled.current = true; prevCount.current = count; return; }
    if (count > prevCount.current) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 4000);
      prevCount.current = count;
      return () => clearTimeout(t);
    }
    prevCount.current = count;
  }, [count]);

  const openApprovals = useCallback(() => setIsOpen(true), []);
  const closeApprovals = useCallback(() => setIsOpen(false), []);

  return (
    <ApprovalsContext.Provider value={{ isOpen, openApprovals, closeApprovals, items, count, flash, writesOn }}>
      {children}
    </ApprovalsContext.Provider>
  );
};

export const useApprovals = (): ApprovalsContextValue => useContext(ApprovalsContext);
